/**
 * Nomination service with CRUD operations
 * Requirements: 2.4, 3.1, 3.2, 5.1, 5.3, 6.1
 */

import {
  Nomination,
  NominationStatus,
  CreateNominationInput,
  UpdateNominationInput,
  ReviewerNote,
  ReviewerNoteInput,
  AuditEntry,
} from '../types/nomination';
import { validateCreateNominationInput } from '../types/nominationValidation';
import { DEMO_NOMINATIONS, DEMO_AUDIT_ENTRIES } from './mockNominationData';

const FEED_PAGE_SIZE = 10;

export interface PaginatedFeed {
  items: Nomination[];
  total: number;
  page: number;
  totalPages: number;
}

export interface NominationServiceResult {
  success: boolean;
  nomination?: Nomination;
  errors: string[];
}

/**
 * In-memory nomination data store for demo mode.
 * Exported for testing purposes.
 */
export class NominationService {
  private nominations: Nomination[];
  private auditEntries: AuditEntry[];

  constructor(
    initialNominations?: Nomination[],
    initialAuditEntries?: AuditEntry[]
  ) {
    this.nominations = initialNominations
      ? [...initialNominations]
      : [...DEMO_NOMINATIONS];
    this.auditEntries = initialAuditEntries
      ? [...initialAuditEntries]
      : [...DEMO_AUDIT_ENTRIES];
  }

  /** Get all nominations (internal). */
  getAllNominationsRaw(): Nomination[] {
    return [...this.nominations];
  }

  /** Get all audit entries (internal). */
  getAuditEntries(): AuditEntry[] {
    return [...this.auditEntries];
  }

  // ---- CRUD ----

  /**
   * Create a nomination.
   * Requirements: 2.2, 2.5
   */
  createNomination(
    input: CreateNominationInput,
    nominatorId: string,
    nominatorName: string,
    asDraft: boolean = false
  ): NominationServiceResult {
    const validation = validateCreateNominationInput(input);
    if (!validation.isValid) {
      return { success: false, errors: validation.errors };
    }

    const ts = new Date().toISOString();
    const nomination: Nomination = {
      id: `nom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      nomineeId: input.nomineeId,
      nomineeName: input.nomineeName,
      nominatorId,
      nominatorName,
      awardType: input.awardType,
      /* Carried through here too. This is the path demo mode and the tests take, and a team
         nomination that loses its roster the moment it is created would make the whole flow look
         broken in exactly the environment people try it in first. */
      nominationType: input.nominationType,
      teamName: input.teamName,
      teamMembers: input.teamMembers,
      reason: input.reason,
      supportingDetails: input.supportingDetails,
      status: asDraft ? NominationStatus.DRAFT : NominationStatus.SUBMITTED,
      reviewerNotes: [],
      createdAt: ts,
      updatedAt: ts,
    };

    this.nominations.push(nomination);
    return { success: true, nomination, errors: [] };
  }

  /**
   * Update a nomination (admin).
   * Requirements: 6.2
   */
  updateNomination(
    nominationId: string,
    updates: UpdateNominationInput,
    adminId: string,
    adminName: string
  ): NominationServiceResult {
    const idx = this.nominations.findIndex((n) => n.id === nominationId);
    if (idx === -1) {
      return { success: false, errors: ['Nomination not found'] };
    }

    const updated: Nomination = {
      ...this.nominations[idx],
      ...updates,
      updatedAt: new Date().toISOString(),
    };
    this.nominations[idx] = updated;

    this.auditEntries.push({
      id: `audit-${Date.now()}`,
      nominationId,
      action: 'EDITED',
      performedBy: adminId,
      performedByName: adminName,
      details: `Fields updated: ${Object.keys(updates).join(', ')}`,
      timestamp: new Date().toISOString(),
    });

    return { success: true, nomination: updated, errors: [] };
  }

  /**
   * Delete a nomination (admin).
   * Requirements: 6.3
   */
  deleteNomination(
    nominationId: string,
    adminId: string,
    adminName: string
  ): { success: boolean; errors: string[] } {
    const idx = this.nominations.findIndex((n) => n.id === nominationId);
    if (idx === -1) {
      return { success: false, errors: ['Nomination not found'] };
    }

    this.nominations.splice(idx, 1);

    this.auditEntries.push({
      id: `audit-${Date.now()}`,
      nominationId,
      action: 'DELETED',
      performedBy: adminId,
      performedByName: adminName,
      details: `Nomination ${nominationId} deleted`,
      timestamp: new Date().toISOString(),
    });

    return { success: true, errors: [] };
  }

  /**
   * Get a single nomination by ID.
   */
  getNomination(nominationId: string): Nomination | undefined {
    return this.nominations.find((n) => n.id === nominationId);
  }

  // ---- Filtered queries ----

  /**
   * Get nominations created by a specific manager.
   * Requirements: 2.4
   */
  getMyNominations(nominatorId: string): Nomination[] {
    return this.nominations.filter((n) => n.nominatorId === nominatorId);
  }

  /**
   * Get the review queue: nominations with Submitted or Under Review status.
   * Requirements: 3.1
   */
  getReviewQueue(): Nomination[] {
    return this.nominations.filter(
      (n) =>
        n.status === NominationStatus.SUBMITTED ||
        n.status === NominationStatus.UNDER_REVIEW
    );
  }

  /**
   * Get the published nominations feed, sorted by publishedAt descending.
   * Supports pagination with 10 items per page.
   * Requirements: 5.1, 5.3
   */
  getFeed(page: number = 1): PaginatedFeed {
    const published = this.nominations
      .filter((n) => n.status === NominationStatus.PUBLISHED)
      .sort((a, b) => {
        const dateA = a.publishedAt || '';
        const dateB = b.publishedAt || '';
        return dateB.localeCompare(dateA);
      });

    const total = published.length;
    const totalPages = Math.max(1, Math.ceil(total / FEED_PAGE_SIZE));
    const safePage = Math.max(1, Math.min(page, totalPages));
    const start = (safePage - 1) * FEED_PAGE_SIZE;
    const items = published.slice(start, start + FEED_PAGE_SIZE);

    return { items, total, page: safePage, totalPages };
  }

  /**
   * Get all nominations regardless of status (admin view).
   * Requirements: 6.1
   */
  getAllNominations(): Nomination[] {
    return [...this.nominations];
  }

  /**
   * Add a reviewer note to a nomination.
   * Requirements: 3.2
   */
  addReviewerNote(
    input: ReviewerNoteInput,
    reviewerId: string,
    reviewerName: string
  ): { success: boolean; note?: ReviewerNote; errors: string[] } {
    const idx = this.nominations.findIndex(
      (n) => n.id === input.nominationId
    );
    if (idx === -1) {
      return { success: false, errors: ['Nomination not found'] };
    }

    if (!input.content || input.content.trim().length === 0) {
      return { success: false, errors: ['Note content is required'] };
    }

    const note: ReviewerNote = {
      id: `note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      nominationId: input.nominationId,
      reviewerId,
      reviewerName,
      content: input.content,
      createdAt: new Date().toISOString(),
    };

    this.nominations[idx] = {
      ...this.nominations[idx],
      reviewerNotes: [...this.nominations[idx].reviewerNotes, note],
      updatedAt: new Date().toISOString(),
    };

    return { success: true, note, errors: [] };
  }
}

/** Default singleton for demo mode. */
export const nominationService = new NominationService();
